import { useState } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  ButtonBase,
  alpha
} from '@mui/material';
import {
  Delete,
  Add,
  ArrowBack,
  HistoryOutlined,
  PaymentsOutlined,
  CreditCardOutlined,
  CheckCircle,
  SvgIconComponent
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { campaignService } from '@/data/campaignService';
import { Campaign } from '@/types/Campaign.interface';
import { User } from '@/types/User.interface';
import { Role } from '@/enums/role';
import UserAutocomplete from '@/components/UserAutocomplete';
import CampaignAutocomplete from '@/components/CampaignAutocomplete';
import { PurchaseType } from '@/enums/purchase-type';
import {
  BatchCollectedPaymentType,
  BatchCustomerDataPayload
} from '@/types/BatchCustomerData.interface';
import { formatCurrency } from '@/utils/formatters';

// PREPAID = parası önceden alındı (sadece hak aktarımı, ciroya yansımaz)
type PaymentChoice = 'PREPAID' | BatchCollectedPaymentType;

interface BatchRow {
  user: User | null;
  campaign: Campaign | null;
  remainingRights: string;
  payment: PaymentChoice;
}

const emptyRow = (): BatchRow => ({
  user: null,
  campaign: null,
  remainingRights: '',
  payment: 'PREPAID'
});

const PAYMENT_OPTIONS: Array<{
  value: PaymentChoice;
  labelKey: string;
  Icon: SvgIconComponent;
}> = [
  {
    value: 'PREPAID',
    labelKey: 'batch.payment.already.collected',
    Icon: HistoryOutlined
  },
  {
    value: PurchaseType.CASH,
    labelKey: 'purchase.type.cash',
    Icon: PaymentsOutlined
  },
  {
    value: PurchaseType.PHYSICAL_CARD,
    labelKey: 'batch.payment.card',
    Icon: CreditCardOutlined
  }
];

const PageHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ mb: 2 }}
    >
      <Box display="flex" alignItems="center">
        <ArrowBack
          sx={{ mr: 1, cursor: 'pointer' }}
          onClick={() => navigate('/management/packages')}
          className="back-button"
        />
        <Typography variant="h3" component="h3">
          {t('batch.customer.data.entry')}
        </Typography>
      </Box>
    </Box>
  );
};

const AddCustomerData = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState<BatchRow[]>([emptyRow()]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openNotification, setOpenNotification] = useState<boolean>(false);

  const handleChange = (
    index: number,
    field: keyof BatchRow,
    value: string | User | Campaign | null
  ) => {
    const updated = [...rows];
    updated[index][field] = value as any;
    setRows(updated);
  };

  const isDuplicateRow = (currentIndex: number): boolean => {
    const currentRow = rows[currentIndex];
    if (!currentRow.user || !currentRow.campaign) return false;

    return rows.some((row, index) => {
      if (index === currentIndex) return false;
      return (
        row.user?.id === currentRow.user?.id &&
        row.campaign?.id === currentRow.campaign?.id
      );
    });
  };

  const handleAddRow = () => {
    setRows([...rows, emptyRow()]);
  };

  const handleRemoveRow = (index: number) => {
    if (rows.length > 1) {
      const updated = [...rows];
      updated.splice(index, 1);
      setRows(updated);
    }
  };

  const validateRows = (): string | null => {
    const userCampaignCombinations = new Map<string, number>();

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (!row.user || !row.campaign || !row.remainingRights) {
        return t('error.all.fields.required');
      }

      const combinationKey = `${row.user.id}-${row.campaign.id}`;
      if (userCampaignCombinations.has(combinationKey)) {
        const firstRowIndex = userCampaignCombinations.get(combinationKey)! + 1;
        const currentRowIndex = i + 1;
        return t('error.duplicate.user.campaign', {
          user: `${row.user.name} ${row.user.surname}`,
          campaign: row.campaign.title,
          firstRow: firstRowIndex,
          currentRow: currentRowIndex
        });
      }
      userCampaignCombinations.set(combinationKey, i);

      const remainingRights = parseInt(row.remainingRights);
      if (isNaN(remainingRights) || remainingRights < 0) {
        return t('error.invalid.remaining.rights');
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateRows();
    if (validationError) {
      setError(validationError);
      setOpenNotification(true);
      return;
    }

    setLoading(true);
    try {
      const payload: BatchCustomerDataPayload = {
        entries: rows.map((row) => ({
          userId: row.user!.id,
          campaignId: row.campaign!.id,
          remainingRights: parseInt(row.remainingRights),
          ...(row.payment !== 'PREPAID' && {
            collectedPaymentType: row.payment
          })
        }))
      };

      await campaignService.batchCustomerDataEntry(payload);

      setSuccess(t('batch.customer.data.success'));
      setError(null);
      setOpenNotification(true);

      // Reset form after success
      setRows([emptyRow()]);
    } catch (err) {
      console.error('Error saving batch customer data:', err);
      const message = err?.response?.data?.message;
      const messages: string[] = Array.isArray(message)
        ? message
        : message
          ? [message]
          : [];
      if (
        messages.some(
          (m) =>
            m.includes('exceeds bundle total count') ||
            m.includes('payment cannot be collected') ||
            m.includes('Payment can only be collected')
        )
      ) {
        setError(messages.join(' '));
      } else {
        setError(t('batch.customer.data.error'));
      }
      setOpenNotification(true);
    } finally {
      setLoading(false);
    }
  };

  // Kutunun altındaki açıklama: tahsilatta ciroya yansıyacak tutar. Tutarı
  // sunucu paket fiyatından kendisi alır; burası sadece bilgi.
  const paymentCaption = (row: BatchRow, value: PaymentChoice): string => {
    if (value === 'PREPAID') return t('batch.payment.already.collected.help');
    const price = row.campaign?.campaignBundle?.bundle?.price;
    if (price === undefined || price === null) {
      return t('batch.payment.collected.help.noprice');
    }
    return t('batch.payment.collected.help', {
      amount: formatCurrency(Number(price))
    });
  };

  const handleCloseNotification = () => {
    setOpenNotification(false);
  };

  return (
    <>
      <Helmet>
        <title>{t('batch.customer.data.entry')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader />
      </PageTitleWrapper>

      <Container maxWidth={false} sx={{ maxWidth: '90%' }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body2" mb={3} color="text.secondary">
            {t('batch.customer.data.description')}
          </Typography>

          {rows.map((row, index) => {
            const isDuplicate = isDuplicateRow(index);
            return (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isDuplicate ? 'error.main' : 'divider',
                  backgroundColor: (theme) =>
                    isDuplicate
                      ? alpha(theme.palette.error.main, 0.05)
                      : 'transparent'
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <UserAutocomplete
                      value={row.user}
                      onChange={(user) => handleChange(index, 'user', user)}
                      roles={[Role.CUSTOMER]}
                      error={isDuplicate}
                      helperText={
                        isDuplicate ? t('error.duplicate.detected') : undefined
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <CampaignAutocomplete
                      value={row.campaign}
                      onChange={(campaign) =>
                        handleChange(index, 'campaign', campaign)
                      }
                      error={isDuplicate}
                      helperText={
                        isDuplicate ? t('error.duplicate.detected') : undefined
                      }
                      // Hak yalnız etkinlik paketinde kullanılır; kombo fiyatın
                      // (BUNDLE_PRODUCT) harcanan bir hakkı yok.
                      types={['BUNDLE_ACTIVITY']}
                    />
                  </Grid>

                  <Grid item xs={10} sm={3}>
                    <TextField
                      type="number"
                      label={t('remaining.rights')}
                      value={row.remainingRights}
                      onChange={(e) =>
                        handleChange(index, 'remainingRights', e.target.value)
                      }
                      inputProps={{ min: 0 }}
                      fullWidth
                      error={isDuplicate}
                    />
                  </Grid>

                  <Grid item xs={2} sm={1} textAlign="right">
                    <IconButton
                      onClick={() => handleRemoveRow(index)}
                      color="error"
                      disabled={rows.length === 1}
                      aria-label={t('remove')}
                    >
                      <Delete />
                    </IconButton>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      mb={1}
                    >
                      {t('batch.payment')}
                    </Typography>
                    <Box
                      role="radiogroup"
                      aria-label={t('batch.payment')}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(3, 1fr)'
                        },
                        gap: 1.5
                      }}
                    >
                      {PAYMENT_OPTIONS.map(({ value, labelKey, Icon }) => {
                        const selected = row.payment === value;
                        return (
                          <ButtonBase
                            key={value}
                            role="radio"
                            aria-checked={selected}
                            onClick={() =>
                              handleChange(index, 'payment', value)
                            }
                            sx={(theme) => ({
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 1.5,
                              width: '100%',
                              px: 2,
                              py: 1.5,
                              textAlign: 'left',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: selected
                                ? theme.palette.primary.main
                                : theme.palette.divider,
                              boxShadow: selected
                                ? `0 0 0 1px ${theme.palette.primary.main}`
                                : 'none',
                              backgroundColor: selected
                                ? alpha(theme.palette.primary.main, 0.08)
                                : theme.palette.background.paper,
                              transition: theme.transitions.create([
                                'border-color',
                                'background-color',
                                'box-shadow'
                              ]),
                              '&:hover': {
                                borderColor: theme.palette.primary.main
                              },
                              '&.Mui-focusVisible': {
                                boxShadow: `0 0 0 2px ${alpha(
                                  theme.palette.primary.main,
                                  0.4
                                )}`
                              }
                            })}
                          >
                            <Icon color={selected ? 'primary' : 'action'} />
                            <Box flex={1} minWidth={0}>
                              <Typography
                                variant="body1"
                                fontWeight={selected ? 700 : 500}
                                color={selected ? 'primary' : 'text.primary'}
                              >
                                {t(labelKey)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {paymentCaption(row, value)}
                              </Typography>
                            </Box>
                            {selected && (
                              <CheckCircle color="primary" fontSize="small" />
                            )}
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}

          <Box display="flex" justifyContent="space-between" mt={3}>
            <Button
              variant="outlined"
              onClick={handleAddRow}
              startIcon={<Add />}
            >
              {t('add.new.row')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : t('batch.save')}
            </Button>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={openNotification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          variant="filled"
          severity={error ? 'error' : 'success'}
          onClose={handleCloseNotification}
        >
          <Typography>{error || success}</Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddCustomerData;
